import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { BookOpen, Clock, ArrowRight, ChevronLeft } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_COLORS = {
  Philosophy: "#2196F3",
  Parenting: "#FFB300",
  "Home & Living": "#4CAF50",
  Advocacy: "#E53935",
  "Local Resources": "#2196F3",
  "Our Vision": "#FFB300",
};

export default function Resources() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(null);

  useEffect(() => {
    axios.get(`${API}/resources`).then((r) => setItems(r.data || [])).catch(() => {});
  }, []);

  if (open) {
    return (
      <div data-testid="page-resource-detail">
        <section className="max-w-3xl mx-auto px-6 md:px-12 pt-16 md:pt-20 pb-16">
          <button
            data-testid="resource-back-btn"
            onClick={() => setOpen(null)}
            className="inline-flex items-center gap-2 text-sm font-medium text-[#2196F3] hover:underline"
          >
            <ChevronLeft className="w-4 h-4" /> Back to all resources
          </button>
          <div className="mt-6 text-xs uppercase tracking-[0.2em] font-semibold" style={{ color: CATEGORY_COLORS[open.category] || "#1F2937" }}>
            {open.category}
          </div>
          <h1 className="mt-3 font-heading font-bold tracking-tighter text-[#1F2937] text-4xl sm:text-5xl leading-[1.02]">
            {open.title}
          </h1>
          <div className="mt-4 text-sm text-[#6B7280] flex items-center gap-3">
            <span>{new Date(open.published_at).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" })}</span>
            <span>·</span>
            <span className="inline-flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> {open.read_time} read</span>
          </div>
          <article className="prose prose-lg mt-8 text-[#374151] leading-relaxed whitespace-pre-line">
            {open.body}
          </article>
        </section>
      </div>
    );
  }

  return (
    <div data-testid="page-resources">
      <section className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 pt-16 md:pt-24 pb-8">
        <div className="text-xs uppercase tracking-[0.2em] text-[#6B7280] font-semibold">Resources & Blog</div>
        <h1 className="mt-4 font-heading font-bold tracking-tighter text-[#1F2937] text-5xl sm:text-6xl lg:text-7xl leading-[0.95]">
          Knowledge,<br />
          <span className="brush-underline brush-blue">gently held.</span>
        </h1>
        <p className="mt-6 max-w-2xl text-lg text-[#4B5563] leading-relaxed">
          Practical guides, philosophy, and local-Marion know-how — written for the families we love.
        </p>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-12">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map((r) => {
            const c = CATEGORY_COLORS[r.category] || "#2196F3";
            return (
              <button
                key={r.id}
                data-testid={`resource-card-${r.id}`}
                onClick={() => setOpen(r)}
                className="text-left rounded-3xl bg-white border border-black/5 p-7 hover:-translate-y-0.5 hover:shadow-sm group"
              >
                <div className="flex items-center justify-between">
                  <span className="text-[0.7rem] tracking-[0.2em] font-semibold uppercase" style={{ color: c }}>{r.category}</span>
                  <BookOpen className="w-4 h-4 text-[#9CA3AF]" strokeWidth={1.6} />
                </div>
                <h3 className="mt-4 font-heading text-xl font-semibold text-[#1F2937] tracking-tight leading-snug group-hover:text-[#2196F3]">{r.title}</h3>
                <p className="mt-3 text-sm text-[#4B5563] leading-relaxed">{r.excerpt}</p>
                <div className="mt-5 flex items-center justify-between text-xs text-[#6B7280]">
                  <span className="inline-flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> {r.read_time}</span>
                  <span className="inline-flex items-center gap-1 font-medium text-[#1F2937]">Read <ArrowRight className="w-3.5 h-3.5" /></span>
                </div>
              </button>
            );
          })}
        </div>
      </section>
    </div>
  );
}
